/**
 * Parser for mermaid's `pie` grammar, so chat renders pie fences with the
 * same interactive chart library as xy charts.
 *
 *   pie [showData] [title Text]
 *       title Text
 *       "Label" : 41
 */
import { paletteFromFrontMatter, splitMermaidSource, type FigurePalette } from "./mermaidSource";

export interface PieSlice {
  readonly label: string;
  readonly value: number;
}

export interface PieChartSpec {
  readonly title: string | null;
  /** mermaid's flag to print values next to the labels. */
  readonly showData: boolean;
  readonly slices: ReadonlyArray<PieSlice>;
  readonly palette: FigurePalette | null;
}

const HEADER_REGEX = /^pie(?:\s+showData)?(?:\s+title\s+(.+))?\s*$/i;
const SLICE_REGEX = /^"([^"]*)"\s*:\s*([+-]?(?:\d+\.?\d*|\.\d+))\s*$/;

export function isPieChartSource(code: string): boolean {
  const firstLine = splitMermaidSource(code).lines[0];
  return firstLine != null && HEADER_REGEX.test(firstLine);
}

/** Null when the body is not a complete pie chart, so the source view stays. */
export function parsePieChart(code: string): PieChartSpec | null {
  const { frontMatter, lines, unterminated } = splitMermaidSource(code);
  if (unterminated) return null;
  const header = lines[0];
  if (header == null) return null;
  const headerMatch = header.match(HEADER_REGEX);
  if (!headerMatch) return null;

  let title = headerMatch[1]?.trim() ?? null;
  // The flag only counts directly after `pie`; later it is title text.
  const showData = /^pie\s+showData(?:\s|$)/i.test(header);
  const slices: PieSlice[] = [];
  let inAccDescrBlock = false;

  for (const line of lines.slice(1)) {
    if (inAccDescrBlock) {
      if (line.includes("}")) inAccDescrBlock = false;
      continue;
    }
    if (/^accTitle\s*:/i.test(line) || /^accDescr\s*:/i.test(line)) continue;
    if (/^accDescr\s*\{/i.test(line)) {
      inAccDescrBlock = !line.includes("}");
      continue;
    }
    const titleMatch = line.match(/^title\s+(.+)$/i);
    if (titleMatch?.[1]) {
      title = titleMatch[1].trim();
      continue;
    }
    const sliceMatch = line.match(SLICE_REGEX);
    if (!sliceMatch?.[2]) return null;
    const value = Number(sliceMatch[2]);
    if (!Number.isFinite(value) || value < 0) return null;
    slices.push({ label: sliceMatch[1] ?? "", value });
  }

  if (slices.length === 0 || inAccDescrBlock) return null;
  // Shares divide by the total; an overflowing sum would read as 0% everywhere.
  if (!Number.isFinite(slices.reduce((sum, slice) => sum + slice.value, 0))) return null;
  return { title, showData, slices, palette: paletteFromFrontMatter(frontMatter, "pie") };
}

export function pieTotal(spec: PieChartSpec): number {
  return spec.slices.reduce((sum, slice) => sum + slice.value, 0);
}

/** "41.0%" style share, guarding an all-zero chart. */
export function pieShareLabel(value: number, total: number): string {
  if (total <= 0) return "0%";
  const share = (value / total) * 100;
  return `${share >= 10 || share === 0 ? share.toFixed(0) : share.toFixed(1)}%`;
}
